import { ModaSofa } from '@dora/backpack';
import { PoliteReminder, ToggleButtonGroup } from '@dora/components';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { sequenceT } from 'fp-ts/lib/Apply';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import * as Preact from 'preact';
import * as H from 'preact/hooks';
import * as Pr from '../../data/product';
import * as S from '../../data/summary';
import * as Selectors from '../../store/selectors';
import { templatesForCurrentRange } from '../../store/selectors/modaSofa';
import { useSelector } from '../hooks/redux';
import * as ConfigurationCard from './configurationCard';
import { Defaults as UseSofaConfigureDefaults, useSofaConfigure } from './hooks/useSofaConfigure';
import * as SeatConfigurationButtons from './seatConfigurationButtons';
import { SofaFormDescription, SofaShape } from './sofa';
import { useDispatch } from 'react-redux';

const makeFormDescription = (
    /** Number of seats in a left hand side end piece */
    seatsPerEndPieceL: number,
    /** Number of seats in a right hand side end piece */
    seatsPerEndPieceR: number,
    /** Number of seats in a straight centre (non-corner, non-end) piece */
    seatsPerStraightPiece: number,
    templateCornerStyles: string[],
    defaultCornerStyle: ModaSofa.CornerStyleT,
    /** Is a curved sofa - treat as straight*/
    curvedSofa: boolean,
): SofaFormDescription => {
    if (seatsPerStraightPiece !== 1) {
        // TODO: handle case where straight seats per piece is not one if it ever comes up
        throw new Error(
            'a straight sofa piece does not have a seat size of one - this may result in unexpected behaviour',
        );
    }

    const corners = !curvedSofa
        ? parseCornerStyles(templateCornerStyles, defaultCornerStyle)
        : undefined;

    const shapes: Array<SofaShape> = curvedSofa
        ? [
              {
                  shape: 'straight',
                  seats: [
                      {
                          position: 'straight',
                          displayName: 'Seats',
                          minimum: seatsPerEndPieceL + seatsPerEndPieceR + 1,
                          maximum: 9,
                      },
                  ],
              },
          ]
        : [
              {
                  shape: 'corner',
                  seats: [
                      {
                          position: 'right',
                          displayName: 'Right Seats',
                          minimum: seatsPerEndPieceR,
                          maximum: 5,
                      },
                      {
                          position: 'left',
                          displayName: 'Left Seats',
                          minimum: seatsPerEndPieceL,
                          maximum: 5,
                      },
                  ],
              },
              {
                  shape: 'u-shape',
                  seats: [
                      { position: 'middle', displayName: 'Middle Seats', minimum: 1, maximum: 5 },
                      {
                          position: 'right',
                          displayName: 'Right Seats',
                          minimum: seatsPerEndPieceR,
                          maximum: 5,
                      },
                      {
                          position: 'left',
                          displayName: 'Left Seats',
                          minimum: seatsPerEndPieceL,
                          maximum: 5,
                      },
                  ],
              },
              {
                  shape: 'straight',
                  seats: [
                      {
                          position: 'straight',
                          displayName: 'Seats',
                          minimum: seatsPerEndPieceL + seatsPerEndPieceR,
                          maximum: 5,
                      },
                  ],
              },
          ];

    return {
        corners,
        shapes,
    };
};

const supportedCornerStyles = ['square', 'angled'];

const parseCornerStyles = (
    templateCornerStyles: string[],
    defaultCornerStyle: ModaSofa.CornerStyleT,
): { possibleStyles: ModaSofa.CornerStyleT[]; currentStyle: ModaSofa.CornerStyleT } => {
    const areAllSupported = templateCornerStyles.every(c => supportedCornerStyles.includes(c));
    if (!areAllSupported) {
        throw new Error(
            `ConfigureModaSofa: not all corner styles are supported - ${templateCornerStyles}`,
        );
    }

    const possibleStyles: ModaSofa.CornerStyleT[] = [];
    templateCornerStyles.forEach(
        c =>
            ModaSofa.isCornerStyle(c) &&
            supportedCornerStyles.includes(c) &&
            possibleStyles.push(c),
    );
    if (possibleStyles.length === 0) {
        throw new Error(
            `ConfigureModaSofa: No supported corner styles received, behaviour undefined`,
        );
    }

    let currentStyle = defaultCornerStyle;
    const isDefaultSupported = supportedCornerStyles.includes(defaultCornerStyle);
    if (!isDefaultSupported) {
        if (possibleStyles.length > 0) {
            currentStyle = possibleStyles[0];
        }
        throw new Error(`ConfigureModaSofa: default corner style is not supported`);
    }

    return { possibleStyles, currentStyle };
};

/**
 * TODO: it would be best to derive this from the SofaFormDescription. The mapping currently is not
 * obvious, as `useSofaConfigure` works with a single default value per left/right/middle/straight
 * seats - the state is shared across the whole description. However, the form description has
 * different values for each shape of sofa.
 *
 * While `useSofaConfigure` could model the form in a more 1:1 way, this doesn't feel worth the
 * effort for the ability to derive the form defaults from the description. And while we could add a
 * default field to each seat description and use those to derive a defaults object, this isn't
 * a sensible way of doing so if defaults differ across shapes.
 *
 */
const defaults: UseSofaConfigureDefaults = {
    shape: 'straight',
    seats: 4,
    middleSeats: 2,
    leftSeats: 2,
    rightSeats: 2,
};

export interface Props {
    currentConfiguration: O.Option<{ sofaId: string; configuration: ModaSofa.Configuration }>;
    loading: boolean;
    onShapeChange: (shape: string) => void;
    onSeatCountChange: (count: number) => void;
    onConfigurationChange: (config: ModaSofa.Configuration) => void;
}

export const Component: Preact.FunctionComponent<Props> = (props: Props) => {
    const templates = useSelector(templatesForCurrentRange);
    const areInterstitialsSupported =
        useSelector(Selectors.ModaSofa.rangeSupportsInterstitials) ?? false;
    if (!templates) return null;

    const leftSeats = templates.end.left.numberOfSeats;
    const rightSeats = templates.end.right.numberOfSeats;
    const middleSeats =
        templates.kind === 'curved'
            ? templates.middle.numberOfSeats
            : templates.straight.numberOfSeats;

    const curveSofa = templates.kind === 'curved';
    const formDescription = makeFormDescription(
        leftSeats,
        rightSeats,
        middleSeats,
        'corner' in templates
            ? Object.entries(templates.corner)
                  .filter(([_, val]) => val !== undefined)
                  .map(([key, _]) => key)
            : [],
        ModaSofa.DefaultCornerStyle,
        curveSofa,
    );

    const toggleButtonGroupSize = 'medium';
    // this sofa should match the currently selected sofa. If there is none currently selected, then
    // it should reflect the sofa specified in the defaults variable.
    const sofa = useSofaConfigure(
        leftSeats,
        rightSeats,
        middleSeats,
        defaults,
        templates.kind,
        pipe(
            props.currentConfiguration,
            O.map(c => c.configuration),
        ),
    );

    H.useEffect(() => {
        if (sofa !== undefined) {
            props.onShapeChange(sofa.shape);
        }
    }, [sofa?.shape]);

    H.useEffect(() => {
        if (sofa !== undefined) {
            props.onSeatCountChange(ModaSofa.countSeats(templates, sofa.configuration));
        }
    }, [sofa?.configuration, templates]);

    H.useEffect(() => {
        if (sofa !== undefined) {
            props.onConfigurationChange(sofa.configuration);
        }
    }, [sofa?.configuration]);

    const getProduct = useSelector(Selectors.ProductData.productBySkuFn);

    const armrestSku = templates.interstitials.drinksCoolerArmrest?.sku;

    const armrestName = pipe(armrestSku && getProduct?.(armrestSku), pr =>
        pr ? Pr.summaryName(pr) : undefined,
    );

    if (!sofa) {
        throw new Error(
            'Sofa configurator not found. This could be a problem loading range sofa pieces',
        );
    }
    return (
        <>
            <Grid container gap={'16px'}>
                {formDescription.shapes.length > 1 && (
                    <ConfigurationCard.Component>
                        <Typography variant="h5">Shape</Typography>
                        <ToggleButtonGroup.Component
                            aria-label="shape"
                            value={sofa.shape}
                            handleChange={(shape: ModaSofa.Shape) => {
                                sofa.setShape(shape);
                                sofa.interstitials.clear();
                            }}
                            options={formDescription.shapes.map(({ shape }) => shape)}
                            size={toggleButtonGroupSize}
                            disabled={props.loading}
                        />
                    </ConfigurationCard.Component>
                )}
                {formDescription.shapes
                    .filter(({ shape }) => sofa.shape === shape)
                    .map(({ seats }) =>
                        seats.map(seat => {
                            const interstitial =
                                sofa.interstitials[
                                    seat.position === 'straight' ? 'middle' : seat.position
                                ];
                            return (
                                <SeatConfigurationButtons.Component
                                    value={sofa.seatCounts[seat.position]}
                                    buttonSize={toggleButtonGroupSize}
                                    onSeatCountChange={sofa.handleSetSeats(seat.position)}
                                    seatCountButtonDisabled={props.loading}
                                    enableInterstitials={
                                        areInterstitialsSupported
                                            ? templates.kind === 'standard'
                                                ? true
                                                : 'advanced'
                                            : false
                                    }
                                    interstitialCount={
                                        sofa.interstitials[
                                            seat.position === 'straight' ? 'middle' : seat.position
                                        ].count
                                    }
                                    maxInterstitials={interstitial.max}
                                    interstitialName={armrestName}
                                    seat={seat}
                                    onInterstitialSelectionChange={count => {
                                        interstitial.set(count);
                                    }}
                                />
                            );
                        }),
                    )}
                {
                    // Not every range of sofa has different corner styles, but this form should
                    // still show this button group if there's just one.
                    sofa.shape !== 'straight' && formDescription.corners ? (
                        <ConfigurationCard.Component>
                            <Typography variant="h5">Corner Seat Shape</Typography>
                            <ToggleButtonGroup.Component
                                aria-label="corner-seat-shape"
                                value={sofa.cornerStyle}
                                handleChange={sofa.setCornerStyle}
                                options={formDescription.corners.possibleStyles}
                                size={toggleButtonGroupSize}
                                disabled={props.loading}
                            />
                        </ConfigurationCard.Component>
                    ) : null
                }
            </Grid>
            {areInterstitialsSupported ? (
                <PoliteReminder.Component
                    title="Please Note"
                    body="Armrests can go in between any sofa pieces. We will place them where we think they will be most useful but you can rearrange them when you receive your order."
                    sx={{ color: 'black', marginTop: '28px' }}
                />
            ) : null}
        </>
    );
};

Component.displayName = 'ConfigureModaSofa_DrawerInner';
